// Lista ticket portale — tabella paginata con StatusPill, link al dettaglio.
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Ticket as TicketIcon, ChevronRight, AlertCircle } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { StatusPill } from '../../components/ui/StatusPill'
import { Skeleton } from '../../components/ui/Skeleton'
import { portalApiCalls } from './portal.api'
import { formatRelativeDate } from '../../lib/utils'
import type { TicketStatus } from '@fieldcore/types'

const PRIORITY_LABEL: Record<string, string> = {
  low: 'Bassa',
  medium: 'Media',
  high: 'Alta',
  critical: 'Critica',
}

const PRIORITY_COLOR: Record<string, string> = {
  low: 'text-gray-500',
  medium: 'text-blue-600',
  high: 'text-amber-600',
  critical: 'text-red-600',
}

export function PortalTicketsListPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'tickets', page],
    queryFn: () => portalApiCalls.getTickets(page, 20).then((r) => r.data),
    placeholderData: (prev) => prev,
  })

  return (
    <div className="space-y-6">
      {/* Intestazione */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">I miei ticket</h1>
          <p className="mt-1 text-sm text-gray-500">
            {data ? `${data.total} ticket totali` : 'Tutte le richieste di intervento'}
          </p>
        </div>
        <Button
          onClick={() => navigate('/portal/tickets/new')}
          leftIcon={<Plus className="h-4 w-4" />}
          size="sm"
        >
          Nuova richiesta
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <AlertCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
          <p className="text-sm text-red-600">
            Impossibile caricare i ticket. Riprova.
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height={80} className="rounded-xl" />
          ))}
        </div>
      ) : data?.items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <TicketIcon className="mb-3 h-12 w-12 text-gray-200" aria-hidden="true" />
          <h3 className="text-base font-semibold text-gray-900">Nessun ticket</h3>
          <p className="mt-1 mb-4 text-sm text-gray-500">
            Non hai ancora aperto nessuna richiesta di intervento.
          </p>
          <Button
            onClick={() => navigate('/portal/tickets/new')}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Apri primo ticket
          </Button>
        </div>
      ) : (
        <>
          {/* Lista ticket */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <ul role="list" className="divide-y divide-gray-100">
              {data?.items.map((ticket) => (
                <li key={ticket.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50"
                    onClick={() => navigate(`/portal/tickets/${ticket.id}`)}
                    aria-label={`Apri ticket ${ticket.ticketNumber}: ${ticket.title}`}
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-4">
                      {/* Indicatore priorità */}
                      <div
                        className={`mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full ${
                          ticket.priority === 'critical'
                            ? 'bg-red-500'
                            : ticket.priority === 'high'
                            ? 'bg-amber-500'
                            : ticket.priority === 'medium'
                            ? 'bg-blue-400'
                            : 'bg-gray-300'
                        }`}
                        aria-label={`Priorità ${PRIORITY_LABEL[ticket.priority] ?? ticket.priority}`}
                      />

                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-gray-900">
                          {ticket.title}
                        </p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs text-gray-400">
                            {ticket.ticketNumber}
                          </span>
                          <span className="text-xs text-gray-300">·</span>
                          <span
                            className={`text-xs ${PRIORITY_COLOR[ticket.priority] ?? 'text-gray-500'}`}
                          >
                            {PRIORITY_LABEL[ticket.priority] ?? ticket.priority}
                          </span>
                          <span className="text-xs text-gray-300">·</span>
                          <span className="text-xs text-gray-400">
                            {formatRelativeDate(ticket.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="ml-4 flex flex-shrink-0 items-center gap-3">
                      <StatusPill
                        status={ticket.status as TicketStatus}
                        size="sm"
                      />
                      <ChevronRight
                        className="h-4 w-4 text-gray-300"
                        aria-hidden="true"
                      />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Paginazione */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Pagina {data.page} di {data.totalPages} — {data.total} ticket
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Precedente
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                  disabled={page === data.totalPages}
                >
                  Successiva
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
